
import { Component, Vue } from 'vue-property-decorator';
import {
  ChatInterface,
  ChatRepositoryInterface,
  FilterRequestData,
  ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import Themeable from 'vuetify/lib/mixins/themeable';
import ChatsRows from '@/components/qa/chat/ChatsRows.vue';
import ChatMessages from '@/components/qa/chat/ChatMessages.vue';
import ChatDetails from '@/components/qa/chat/ChatDetails.vue';
import ChatComments from '@/components/qa/chat/ChatComments.vue';
import ChatPrivateNotes from '@/components/qa/chat/ChatPrivateNotes.vue';
import ChatReview from '@/components/qa/chat/ChatReview.vue';
import ChatsFilters from '@/components/qa/chat/ChatsFilters.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';

interface ProfilesSelectInterface {
  id: string,
  name: string,
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'chatRepositoryV2',
    'profileRepository',
    'userRepository',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
  ],
  components: {
    ChatsRows,
    ChatsFilters,
    ChatMessages,
    ChatDetails,
    ChatComments,
    ChatPrivateNotes,
    ChatReview,
  },
})
export default class Queue extends Vue {
  public $refs!: { chatRows: HTMLFormElement & { filteredChats: ChatInterface[] } };
  private readonly chatRepositoryV2!: ChatRepositoryInterface;
  private readonly userRepository!: UserRepoInterface;
  private readonly profileRepository!: ProfileRepositoryInterface;
  private readonly scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private readonly scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private readonly tagRepositoryV2!: TagRepositoryInterface;

  private loading: boolean = true;

  private chats: ChatInterface[] = [];
  private chat: ChatInterface = {} as ChatInterface;

  private profilesMap: ProfileMapInterface = {};

  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[] = [];
  private profiles: ProfilesSelectInterface[] = [];
  private users: UserBaseInfoInterface[] = [];

  private tags: TagInterface[] = [];

  private disabledChatIds: string[] = [];

  private filter: FilterRequestData | undefined = {};

  private itemsPerPage: number = 15;
  private page: number = 1;
  private hasNextPage: boolean = true;

  private get windowHeight(): number {
    return window.screen.height - 300;
  }

  private async created(): Promise<void> {
    await this.initialize();
    try {
      await this.fetchScores();
      await this.fetchTags();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async initialize(filter?: FilterRequestData): Promise<void> {
    try {
      this.page = 1;
      this.hasNextPage = true;
      this.filter = filter;
      this.chats = await this.chatRepositoryV2.getChatQueue({
        ...filter,
        page: this.page,
      });

      if (this.chats.length < this.itemsPerPage) {
        this.hasNextPage = false;
      }

      if (this.$route.params.chatId) {
        await this.getChat(this.$route.params.chatId);
      }

      if (window.screen.height >= 1300) {
        await this.loadMoreItems();
      }

      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getChat(id: string): Promise<void> {
    if (this.chat.id === id) {
      return;
    }

    try {
      this.loading = true;
      const chat = await this.chatRepositoryV2.getChatById(id);
      if (!this.$route.params.chatId || this.$route.params.chatId !== id) {
        this.$route.params.chatId = chat.id;
        await this.$router.replace({ name: 'chat_queue', params: { chatId: chat.id } });
      }
      await this.fetchChatProfilesData(chat);
      this.chat = chat;
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchTags(): Promise<void> {
    this.tags = await this.tagRepositoryV2.getActiveTags();
  }

  private async fetchScores(): Promise<void> {
    this.scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    this.scoreGrades = await this.scoreGradeRepositoryV2.getAll();
  }

  private async fetchChatProfilesData(chat: ChatInterface): Promise<void> {
    this.profiles = [];

    const idsForReview = await this.extractUserIdsForReview(chat);
    const idsFromChatData = this.extractUserIdsFromChatData(chat);
    const existingReviewIds = this.extractExistingReviewUsersIds(chat);

    const profiles = await this.profileRepository
      .getBaseProfileDataByUsersIds(
        [...idsFromChatData, ...idsForReview, ...existingReviewIds]
          .filter((id, index, self) => self.indexOf(id) === index),
      );

    this.profiles.push({
      id: 'chat',
      name: 'Chat Overall',
    });

    const profilesForReview = profiles.filter((profile) => idsForReview.includes(profile.userId))
      .map((profile) => ({
        id: profile.userId,
        name: `${profile.firstName} ${profile.lastName}`,
      }));

    this.profiles.push(...profilesForReview);

    const [firstName, lastName] = this.$store.getters.name.split(' ');
    this.profilesMap[this.$store.getters.id] = {
      userId: this.$store.getters.id,
      profileId: '',
      firstName,
      lastName,
      fullName: '',
    };

    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private extractUserIdsFromChatData(chat: ChatInterface): string[] {
    const ids: string[] = [];
    chat.comments.forEach((comment) => {
      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    chat.notes.forEach((note) => {
      if (!ids.includes(note.author)) {
        ids.push(note.author);
      }
    });

    chat.messages.forEach((message) => {
      if (!message.comments.length) {
        return;
      }

      message.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    return ids;
  }

  private extractExistingReviewUsersIds(chat: ChatInterface): string[] {
    if (!chat.reviews.length) {
      return [];
    }

    const ids: string[] = [];
    chat.reviews.forEach((review) => {
      if (!ids.includes(review.reviewerId)) {
        ids.push(review.reviewerId);
      }

      if (!ids.includes(review.reviewedId)) {
        ids.push(review.reviewerId);
      }

      if (!review.comments.length) {
        return;
      }

      review.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    return ids;
  }

  private async extractUserIdsForReview(chat: ChatInterface): Promise<string[]> {
    const ids: string[] = [];
    const emails = chat.agents.map((agent) => agent.email);
    emails.push(
      ...chat.supervisors.map((supervisor) => supervisor.email),
    );

    const users = await this.userRepository.getUsersByEmails(emails);
    const missingUsers = emails.filter((userEmail) => !users.map((user) => user.email).includes(userEmail));
    this.users = users;

    if (missingUsers.length) {
      this.disabledChatIds.push(chat.id);
      this.$errorDialog.show(
        `There Are Some Missing Agents (${missingUsers.join(', ')})...
        Please register them before review the chat!`,
      );
      return [];
    }

    users.forEach((user) => {
      if (!ids.includes(user.id)) {
        ids.push(user.id);
      }
    });

    return ids;
  }

  private async filterChats(chatId: string): Promise<void> {
    const index = this.chats.findIndex((chat) => chat.id === chatId);
    const nextChatId = this.chats[index + 1]?.id;

    this.chats = this.chats.filter((chat) => chat.id !== chatId);

    if (nextChatId) {
      await this.getChat(nextChatId);
      return;
    }

    this.chat = {} as ChatInterface;
  }

  private async handleScroll(
    event: { target: { scrollTop: number, clientHeight: number, scrollHeight: number } },
  ): Promise<void> {
    if ((event.target.scrollTop + event.target.clientHeight + 10 < event.target.scrollHeight) || !this.hasNextPage) {
      return;
    }

    await this.loadMoreItems();
  }

  private async loadMoreItems(): Promise<void> {
    if (!this.hasNextPage) {
      return;
    }

    this.page += 1;
    try {
      const chats = await this.chatRepositoryV2.getChatQueue({
        ...this.filter,
        page: this.page,
      });

      if (chats.length < this.itemsPerPage) {
        this.hasNextPage = false;
      }

      this.chats.push(...chats);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async checkChatsTable(): Promise<void> {
    const columnFilled = this.$refs.chatRows.filteredChats.length >= this.itemsPerPage;
    if (!this.hasNextPage || columnFilled) {
      return;
    }

    await this.loadMoreItems();
  }
}
